/**
 * localStorage utilities.
 *
 * App state now lives in IndexedDB (see persistence.ts). localStorage is kept
 * only for the theme preference — which the FOUC-avoidance script in
 * public/index.html must read synchronously before the bundle loads — plus a
 * couple of pure storage-status helpers used by the save indicator.
 */

import { logger } from './logger';

// NOTE: This key string is duplicated in the FOUC-avoidance inline script in
// public/index.html. The script runs before the bundle loads and cannot import
// this constant, so keep the two literals in sync if either ever changes.
const THEME_KEY = 'better-tdl-theme';

/**
 * Persist the user's theme preference. Stored under its own key (not with app
 * state) because the theme is global and must survive even when no file is
 * loaded, and must be readable synchronously at first paint.
 */
export const saveThemePreference = (theme: 'light' | 'dark'): void => {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch (error) {
    logger.error('Failed to save theme preference to localStorage', error);
  }
};

/**
 * Load the persisted theme preference, or null if none/invalid is stored.
 * A null result means "use the default" (light) for first-time visitors.
 */
export const loadThemePreference = (): 'light' | 'dark' | null => {
  try {
    const value = localStorage.getItem(THEME_KEY);
    return value === 'dark' || value === 'light' ? value : null;
  } catch (error) {
    logger.error('Failed to load theme preference from localStorage', error);
    return null;
  }
};

/**
 * Check if localStorage is available (for older browsers or private mode)
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Format bytes to MB with 2 decimal places
 */
export const formatBytes = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return mb.toFixed(2);
};

/**
 * Get storage usage status with color coding.
 *
 * @param usedBytes  Bytes currently used.
 * @param limitBytes Budget to compare against (defaults to 10 MB; callers using
 *                   IndexedDB pass the real quota from `getStorageEstimate`).
 */
export const getStorageStatus = (
  usedBytes: number,
  limitBytes: number = 10 * 1024 * 1024
): { color: string; isWarning: boolean; isCritical: boolean } => {
  const usagePercent = limitBytes > 0 ? (usedBytes / limitBytes) * 100 : 0;

  if (usagePercent >= 90) {
    return { color: '#dc3545', isWarning: false, isCritical: true }; // Red - critical
  } else if (usagePercent >= 75) {
    return { color: '#ffc107', isWarning: true, isCritical: false }; // Yellow - warning
  } else {
    return { color: '#28a745', isWarning: false, isCritical: false }; // Green - normal
  }
};
